<script>

    import { Loader } from '@googlemaps/js-api-loader'
    import { createEventDispatcher, onMount } from 'svelte'

    const { GOOGLE_MAPS_KEY } = _CONSTANTS_
    const dispatch = createEventDispatcher()
    const TRUCK_PATH = 'M624 352h-16V243.9c0-12.7-5.1-24.9-14.1-33.9L494 110.1c-9-9-21.2-14.1-33.9-14.1H416V48c0-26.5-21.5-48-48-48H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h16c0 53 43 96 96 96s96-43 96-96h128c0 53 43 96 96 96s96-43 96-96h48c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zM160 464c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm320 0c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm80-208H416V144h44.1l99.9 99.9V256z'

    export let drivers = []
    export let route = null
    export let selectedId = ''
    export let day = ''
    export let routeNote = false
    export let focus = null
    export let focusSeq = 0
    export let delivered = {}

    let mapEl
    let map
    let drawn = []
    let fitKey = ''
    let missingKey = !GOOGLE_MAPS_KEY
    let mapError = false

    onMount(() => {
        if (!GOOGLE_MAPS_KEY || !mapEl) return

        const loader = new Loader({
            apiKey: GOOGLE_MAPS_KEY,
            version: 'weekly',
        })

        loader.load().then(() => {
            map = new google.maps.Map(mapEl, {
                center: { lat: 19.4326, lng: -99.1332 },
                zoom: 11,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            })
        }).catch(() => {
            mapError = true
        })
    })

    $: if (map)
        draw(drivers, route, selectedId, day, focus, focusSeq, delivered)

    function draw() {
        if (!map) return

        drawn.forEach(item => item.setMap(null))
        drawn = []

        const points = []
        const path = linePath(route)

        if (path.length) {
            drawn.push(new google.maps.Polyline({
                map,
                path,
                strokeColor: '#098E37',
                strokeWeight: 4,
            }))
            path.forEach(point => points.push(point))
        }

        ;(route && route.stops || []).forEach((stop, index) => {
            if (!stop.position || stop.position.lat == null) return
            const done = stopDelivered(stop)
            drawn.push(new google.maps.Marker({
                map,
                position: stop.position,
                label: {
                    text: String(stop.order || index + 1),
                    color: done ? '#ffffff' : '#1a1a1a',
                    fontSize: '11px',
                    fontWeight: '700',
                },
                icon: done ? {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 12,
                    fillColor: '#098E37',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                } : undefined,
            }))
            points.push(stop.position)
        })

        ;(drivers || []).forEach(driver => {
            const position = driver.position
            if (!position || position.lat == null || position.lng == null) return

            const selected = String(driver.driverId) === String(selectedId)
            const name = (driver.name || 'Driver').trim()
            const marker = new google.maps.Marker({
                map,
                position: { lat: position.lat, lng: position.lng },
                title: name,
                zIndex: selected ? 30 : 10,
                label: {
                    text: name.length > 24 ? `${name.slice(0, 23)}…` : name,
                    color: '#1a1a1a',
                    fontSize: '12px',
                    fontWeight: '700',
                    className: 'driver-map-label',
                },
                icon: {
                    path: TRUCK_PATH,
                    scale: 0.05,
                    fillColor: '#F5C400',
                    fillOpacity: 1,
                    strokeColor: selected ? '#1a1a1a' : '#ffffff',
                    strokeWeight: selected ? 2 : 1.5,
                    anchor: new google.maps.Point(320, 256),
                    labelOrigin: new google.maps.Point(320, -280),
                },
            })
            marker.addListener('click', () => dispatch('select', driver.driverId))
            drawn.push(marker)
            points.push({ lat: position.lat, lng: position.lng })
        })

        const stop = focus && focus.lat != null && focus.lng != null
            ? { lat: focus.lat, lng: focus.lng }
            : null
        const driver = driverPosition(selectedId)
        const nextFit = stop
            ? `stop|${focusSeq}`
            : driver
                ? `${day}|${selectedId}|driver`
                : `${day}|all|${path.length}|${(drivers || []).length}`
        if ((!points.length && !stop && !driver) || nextFit === fitKey) return

        fitKey = nextFit

        if (stop || driver) {
            map.setCenter(stop || driver)
            map.setZoom(16)
            return
        }

        if (points.length === 1) {
            map.setCenter(points[0])
            map.setZoom(14)
            return
        }

        const bounds = new google.maps.LatLngBounds()
        points.forEach(point => bounds.extend(point))
        map.fitBounds(bounds)
    }

    function stopDelivered(stop) {
        const ids = (stop && stop.shipmentIds) || []
        return ids.length > 0 && ids.every(id => delivered[String(id)])
    }

    function driverPosition(id) {
        if (!id) return null

        const driver = (drivers || []).find(item => String(item.driverId) === String(id))
        const position = driver && driver.position
        if (!position || position.lat == null || position.lng == null) return null

        return { lat: position.lat, lng: position.lng }
    }

    function linePath(value) {
        if (!value) return []

        const decoded = []

        for (const leg of value.legs || []) {
            if (!leg || !leg.polyline) continue
            decoded.push(...decodePolyline(leg.polyline))
        }

        if (decoded.length) return decoded

        const straight = []
        if (value.start && value.start.lat != null) straight.push(value.start)
        for (const stop of value.stops || []) {
            if (stop.position && stop.position.lat != null) straight.push(stop.position)
        }
        if (value.end && value.end.lat != null) straight.push(value.end)
        return straight
    }

    function decodePolyline(encoded) {
        const points = []
        if (!encoded || typeof encoded !== 'string') return points

        let index = 0
        let lat = 0
        let lng = 0

        while (index < encoded.length) {
            for (const axis of ['lat', 'lng']) {
                let result = 0
                let shift = 0
                let byte

                do {
                    byte = encoded.charCodeAt(index++) - 63
                    result |= (byte & 0x1f) << shift
                    shift += 5
                } while (byte >= 0x20 && index < encoded.length)

                const delta = result & 1 ? ~(result >> 1) : result >> 1

                if (axis === 'lat') lat += delta
                else lng += delta
            }

            points.push({ lat: lat / 1e5, lng: lng / 1e5 })
        }

        return points
    }

</script>

<style>
    .driver-map {
        position: relative;
        height: 560px;
        border-radius: 28px;
        overflow: hidden;
        background: #f4f4f4;
    }

    .driver-map__canvas {
        width: 100%;
        height: 100%;
    }

    .driver-map__note {
        position: absolute;
        z-index: 1;
        top: 12px;
        left: 12px;
        margin: 0;
        padding: 0.35rem 0.7rem;
        border-radius: 999px;
        background: #fff;
        color: #3a3a3a;
        font-size: 12px;
        font-weight: 600;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }

    :global(.driver-map-label) {
        background: #fff;
        border-radius: 999px;
        padding: 2px 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
        white-space: nowrap;
    }
</style>

<div class="driver-map">
    {#if missingKey}
        <p class="driver-map__note">Falta la llave de Google Maps en el admin</p>
    {:else if mapError}
        <p class="driver-map__note">No se pudo cargar el mapa</p>
    {:else if routeNote}
        <p class="driver-map__note">Sin ruta calculada</p>
    {/if}
    <div class="driver-map__canvas" bind:this={mapEl}></div>
</div>
